import { Driver } from '../driver/driver.model';
import { Equipment } from '../equipment/equipment.model';
import { Order } from '../orders/orders.model';
import { OtherExpense } from '../otherExpenses/otherExpense.model';
import { Trip } from '../trips/trips.model';

export interface DocumentAttachmentFilterParams {
  name?: string[];
  type?: DocumentType[];
  driver_id?: string[];
  upload_from?: string;
  upload_to?: string;
  trip_id?: string[];
  order_id?: string[];
  expense_id?: string[];
  visibility?: boolean;
  customer_order_number?: string[];
  submitter_id?: string[];
  reviewed?: boolean;
}

// search document attachments
export interface SearchDocumentAttachmentResponse {
  success: boolean;
  data: SearchDocumentAttachment[];
  size: number;
  total: number;
  _links: {
    self: string;
  };
  more_records: boolean;
}

export interface Attachment {
  id: string;
  name: string;
  reviewed: boolean;
  size: number;
  key: string;
  file_type: string;
  s3_signed_url?: string;
  s3_signed_download_url?: string;
  created_at: string;
  created_by: string | null;
}

export interface SearchDocumentAttachment {
  id: string;
  attachment_id: string;
  name: string;
  size: number;
  file_type: string;
  reviewed: boolean;
  visibility: boolean;
  document_type: DocumentType;
  driver?: Pick<Driver, 'id' | 'first_name' | 'last_name'> | null;
  equipment?: Pick<Equipment, 'id' | 'name'> | null;
  entities_associated: EntityAssociated[];
  created_at: string;
  created_by: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  s3_signed_url?: string;
  s3_signed_download_url?: string;
}

export enum DocumentType {
  proofOfDelivery = 'PROOF_OF_DELIVERY',
  rateConfirmation = 'RATE_CONFIRMATION',
  billOfLading = 'BILL_OF_LADING',
  lumper = 'LUMPER',
  receipt = 'RECEIPT',
  invoice = 'INVOICE',
  other = 'OTHER',
  empty = 'EMPTY',
  tripManifest = 'TRIP_MANIFEST',
  photo = 'PHOTO',
  driverInstructions = 'DRIVER_INSTRUCTIONS',
  permit = 'PERMIT'
}

export interface EntityAssociated {
  id: string;
  entity_type: 'ORDER' | 'TRIP' | 'OTHER_EXPENSE';
  order?:
    | (Pick<Order, 'id' | 'readable_id' | 'customer_order_number'> & {
        customer: { id: string; name: string } | null;
      })
    | null;
  trip?:
    | (Pick<Trip, 'id'> & {
        readable_id: string | number;
        load_id: string | null;
        order_readable_id: string | null;
      })
    | null;
  other_expense?: Pick<OtherExpense, 'id' | 'name' | 'date_from' | 'date_to'> | null;
}

export interface EntityAssociatedById {
  id: string;
  entity_type: 'ORDER' | 'TRIP' | 'OTHER_EXPENSE';
  order?:
    | (Pick<Order, 'id' | 'readable_id' | 'customer_order_number'> & {
        customer: { id: string; name: string } | null;
      })
    | null;
  trip?:
    | (Pick<Trip, 'id'> & {
        readable_id: string | number;
        load_id: string | null;
        order_readable_id: string | null;
      })
    | null;
  other_expense?: Pick<OtherExpense, 'id' | 'name' | 'date_from' | 'date_to'> | null;
}

export interface ISearchDocumentAttachmentsResponse {
  attachments: SearchDocumentAttachment[];
  total: number;
}

// Mark as reviewed
export interface MarkAsReviewedDocumentAttachmentResponse {
  success: boolean;
  data: Attachment[];
}

export interface IMarkAsReviewedDocumentAttachmentResponse {
  attachments: Attachment[];
}

export interface MarkAsReviewedDocumentAttachmentParams {
  filters: DocumentAttachmentFilterParams;
  reviewed: boolean;
}

// Update attachment
export interface UpdateDocumentAttachmentParams {
  id: string;
  name?: string;
  reviewed?: boolean;
  type?: DocumentType;
  visibility?: boolean;
  driver_id?: string | null;
  equipment_id?: string | null;
  trip_id?: string | null;
  order_id?: string | null;
  expense_id?: string | null;

  // previous values to be used for invalidation tags
  previousValues?: {
    expense_id?: string | null;
    order_id?: string | null;
    trip_id?: string | null;
  };
}

// Upload attachment
export type DocumentAttachmentFile = File & Attachment;

export interface UploadDocumentAttachmentParams {
  attachment: DocumentAttachmentFile;
  type?: DocumentType;
  visibility: boolean;
  driverId?: string;
  equipmentId?: string;
  orderId?: string;
  tripId?: string;
  expenseId?: string;
}

export interface UploadDocumentAttachmentResponse {
  success: boolean;
  data: Attachment[];
}

// Get attachment by id
export interface DocumentAttachmentById {
  id: string;
  attachment_id: string;
  name: string;
  size: number;
  file_type: string;
  reviewed: boolean;
  visibility: boolean;
  document_type: DocumentType;
  driver?: Pick<Driver, 'id' | 'first_name' | 'last_name'> | null;
  equipment?: Pick<Equipment, 'id' | 'name'> | null;
  entities_associated: EntityAssociatedById[];
  created_at: string;
  created_by: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  s3_signed_url?: string;
  s3_signed_download_url?: string;
}
export interface GetDocumentAttachmentByIdResponse {
  success: boolean;
  data: DocumentAttachmentById;
}
export interface IGetDocumentAttachmentByIdResponse {
  attachment: DocumentAttachmentById;
}

// Delete attachment
export interface DeleteDocumentAttachmentResponse {
  success: boolean;
  message: string;
}
